/**
 * AI Recruit - Bulk Apply Context
 *
 * Hosts the bulk-apply dialog and its minimized tray ABOVE the route tree, so a
 * running batch survives navigation. Previously the dialog was mounted inside
 * JobDetailPage, so moving to Candidates or Dashboard unmounted it and the
 * recruiter lost the progress view (uploads kept running server-side, but there
 * was nothing to watch or to answer duplicate prompts in).
 *
 * Mounted inside <Router> deliberately: DuplicateDetectionModal uses
 * useNavigate(), so the host needs router context.
 *
 * Scope: survives client-side navigation, NOT a full page reload. Browser File
 * objects can't be serialised, so a queued-but-not-yet-uploaded file cannot be
 * restored after F5. Anything already uploaded keeps processing regardless.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'

import BulkApplyResumesModal from '@/components/matching/BulkApplyResumesModal'

interface BulkApplyTarget {
  jobId: string
  jobTitle?: string
}

interface BulkApplyContextValue {
  /** Open bulk apply for a job. Blocked if another job's batch is still open. */
  openBulkApply: (jobId: string, jobTitle?: string) => void
  /** Job id of the batch currently open, if any. */
  activeJobId: string | null
}

const BulkApplyContext = createContext<BulkApplyContextValue | null>(null)

export const useBulkApply = (): BulkApplyContextValue => {
  const ctx = useContext(BulkApplyContext)
  if (!ctx) throw new Error('useBulkApply must be used within a BulkApplyProvider')
  return ctx
}

export const BulkApplyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [target, setTarget] = useState<BulkApplyTarget | null>(null)
  const queryClient = useQueryClient()

  const openBulkApply = useCallback((jobId: string, jobTitle?: string) => {
    // Refuse to silently discard a batch that's still open for another job —
    // the dialog holds in-memory File objects that can't be recovered.
    setTarget((prev) => {
      if (prev && prev.jobId !== jobId) {
        toast.error(
          'A bulk apply is already open for another job. Finish or close it before starting a new one.',
          { duration: 5000 }
        )
        return prev
      }
      return { jobId, jobTitle }
    })
  }, [])

  const value = useMemo<BulkApplyContextValue>(() => ({
    openBulkApply,
    activeJobId: target?.jobId ?? null,
  }), [openBulkApply, target?.jobId])

  return (
    <BulkApplyContext.Provider value={value}>
      {children}

      {/* Rendered outside the routes, so navigation never unmounts a live batch. */}
      {target && (
        <BulkApplyResumesModal
          isOpen
          jobId={target.jobId}
          jobTitle={target.jobTitle}
          onClose={() => setTarget(null)}
          onCompleted={() => {
            // Refresh the job's results even if the recruiter is on another page,
            // so the table is correct whenever they navigate back.
            queryClient.invalidateQueries({ queryKey: ['comparison-results', target.jobId] })
          }}
        />
      )}
    </BulkApplyContext.Provider>
  )
}

export default BulkApplyProvider
